import { Typography, Grid, Paper, Box, IconButton, Tooltip } from '@mui/material';
import { 
  HeadsetMic, 
  Email, 
  WhatsApp, 
  Assignment, 
  Phone,
  Refresh
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface ImportantNumbersData {
  availableAgentsCount: number;
  needAgentCount: number;
  callsOnHoldCount: number;
  avgEmailResponseTime: number;
  avgWhatsappResponseTime: number;
}

interface ImportantNumbersSectionProps {
  importantNumbers?: ImportantNumbersData;
  onRefresh?: () => void;
}

export const ImportantNumbersSection = ({ importantNumbers, onRefresh }: ImportantNumbersSectionProps) => {
  const { t } = useTranslation();

  const formatResponseTime = (timeInMinutes: number) => {
    if (timeInMinutes === 0) return t('dashboard.kpis.noData');
    
    const hours = Math.floor(timeInMinutes / 60);
    const minutes = timeInMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const kpiData = {
    availableAgents: importantNumbers?.availableAgentsCount ?? 0,
    unassignedCases: importantNumbers?.needAgentCount ?? 0,
    callsOnHold: importantNumbers?.callsOnHoldCount ?? 0,
    avgEmailResponse: formatResponseTime(importantNumbers?.avgEmailResponseTime ?? 0),
    avgWhatsappResponse: formatResponseTime(importantNumbers?.avgWhatsappResponseTime ?? 0)
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t('dashboard.sections.importantNumbers')}
        </Typography>
        {onRefresh && (
          <Tooltip title={t('common.refreshData')}>
            <IconButton
              onClick={onRefresh}
              size="small"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Grid de KPIs */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
            <HeadsetMic color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2" color="textSecondary">
              {t('dashboard.kpis.availableAgents')}
            </Typography>
            <Typography variant="h4" color="success.main">
              {kpiData.availableAgents}
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
            <Assignment color="error" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2" color="textSecondary">
              {t('dashboard.kpis.needAgentCases')}
            </Typography>
            <Typography variant="h4" color="error.main">
              {kpiData.unassignedCases}
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
            <Phone color="warning" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2" color="textSecondary">
              {t('dashboard.kpis.callsOnHold')}
            </Typography>
            <Typography variant="h4" color="warning.main">
              {kpiData.callsOnHold}
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
            <Email color="info" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2" color="textSecondary">
              {t('dashboard.kpis.avgEmailResponse')}
            </Typography>
            <Typography variant="h4" color="info.main">
              {kpiData.avgEmailResponse}
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
            <WhatsApp color="success" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2" color="textSecondary">
              {t('dashboard.kpis.avgWhatsappResponse')}
            </Typography>
            <Typography variant="h4" color="success.main">
              {kpiData.avgWhatsappResponse}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
}; 